<template>
  <div
    id="reviews-wrapper"
    v-observe-visibility="{
      callback: reviewsVisibilityChanged,
      throttle: 300,
      throttleOptions: {
        leading: 'both',
      },
    }"
    class="campsite-reviews"
    :class="[{ loaded: reviewsIsVisible, desktop: $isDesktop }]"
  >
    <campsite-reviews-summary
      v-if="isSummaryVisible"
      :percentages="summary"
      :rate-count="campsiteRateCount"
      :overall-rating="campsiteRating"
      :rating="ratingBreakdown && ratingBreakdown.rateOverall"
      :force-visibility="true"
      :theme-breakdown="themeBreakdown"
      :rating-breakdown="ratingBreakdown"
      :campsite-name="campsiteName"
      :summary-text="summaryText"
    >
      <template #reviews-header-top-row>
        <pu-ratings-distribution
          v-if="$isDesktop && isDistributionVisible"
          v-model="selectedRating"
          :distribution="distribution"
          @input="setRating"
        />
      </template>
    </campsite-reviews-summary>

    <pu-ratings-distribution
      v-if="!$isDesktop && isDistributionVisible"
      v-model="selectedRating"
      :distribution="distribution"
      @input="setRating"
    />

    <div v-if="loading" class="status">
      <loading-spinner />
    </div>
    <div v-if="errorText">
      <p class="status error">
        <span>Error: {{ errorText }}</span>
      </p>
    </div>
    <div v-else-if="displayState === 'no-reviews'" class="no-reviews">
      <translate tag="p" class="status"> No reviews yet. </translate>
      <div class="reviews-action-buttons" data-allow-mismatch>
        <write-review-button
          :campsite-slug="campsiteSlug"
          :campsite-name="campsiteName"
        />
      </div>
    </div>
    <template v-else-if="displayState == 'show'">
      <div class="reviews-action-buttons" data-allow-mismatch>
        <write-review-button
          :campsite-slug="campsiteSlug"
          :campsite-name="campsiteName"
        />
        <div class="reviews-filters-wrapper">
          <campsite-reviews-filters
            :filter-counts="selectedFilterCounts"
            :filter-options="filterOptions"
            :selected-filters="selectedFilters"
            :selected-sort="sorting"
            @filters-and-sort-changed="updateFiltersAndSort"
            @filter-select-changed="updateSelectedFiltersAndCounts"
            @closed="resetFiltersAndCounts"
          />
        </div>
      </div>
      <div class="review-filters">
        <p v-if="filterNames.length" class="applied-filters">
          <translate tag="strong"> Filtered by: </translate>
          <template v-for="filterName in filterNames" :key="filterName">
            <span>{{ filterName }}</span>
          </template>
          <img
            v-if="isFilterApplied"
            @click="resetFilters"
            src="@/assets/images/cross-circle.svg"
            loading="lazy"
            class="reset-icon"
            width="22"
            height="22"
            :alt="$t('Reset Filters')"
          />
        </p>
      </div>

      <div class="campsite-reviews-gallery">
        <campsite-reviews-gallery
          v-if="reviewPhotosInline && reviewPhotosInline.length"
          :photos="reviewPhotosInline"
          :photos-full="reviewPhotosFull"
          @fullscreen-opened="fetchReviewPhotos"
        />
        <div v-if="reviewImagesLoading" class="campsite-reviews-gallery-loader">
          <loading-spinner />
        </div>
      </div>

      <section
        :id="reviewsHash"
        class="reviews-list"
        data-reviews-scroll-target
      >
        <div class="list-header">
          <template v-if="currentPage && totalPages">
            <translate
              tag="div"
              class="reviews-page-count-summary"
              :tt="{ currentPage, totalPages }"
            >
              Reviews page {currentPage} of {totalPages}
            </translate>
          </template>
          <div
            v-if="sortingTranslatedName"
            class="reviews-sorting-name"
            @click="sortFilterClickHandler"
          >
            {{ sortingTranslatedName }}
            <img
              src="@/assets/images/down-rounded.svg"
              width="14"
              height="8"
              alt=""
            />
          </div>
        </div>

        <div class="reviews-list-container">
          <template v-for="review of results" :key="review.id">
            <campsite-single-review :review="review" />
          </template>
        </div>

        <hr />
      </section>

      <lazy-pagination-controls
        v-if="totalPages > 1"
        :pagination-data="paginationData"
        class="pagination"
        @change="changePage"
      />
    </template>
  </div>
</template>

<script setup lang="ts">
import { PaginationData } from '~/utility/pagination'
import {
  reportApiError,
  reportAppError,
} from '~/utility/sentry/apiErrorReporter'
import { ReviewPhotosFetch } from '../ReviewPhotosFetch'
import { ReviewsRepositoryFetch } from '../ReviewsRepositoryFetch'
import { AjaxCampsiteReviewFilterOptionsRepository } from '../review-filter-options/AjaxCampsiteReviewFilterOptionsRepository'
import { CampsiteReviewFilterOption } from '../review-filter-options/domain'
import {
  FilterCounts,
  PhotoResult,
  Rating,
  RatingBreakdown,
  Ratings,
  Review,
  ReviewsResponse,
  ThemeBreakdown,
} from '../types'
import { Filters, SortAndFilters } from './types'

const DEFAULT_SORTING = '-visit_date'

export interface DistRating {
  percentage: number
  count: number
  value: number
}

interface Summary {
  recommend: number
  revisit: number
}

const currentLanguage: string = (useRoute().params.lang as string) || 'en-gb'

const props = withDefaults(
  defineProps<{
    campsiteSlug: string
    campsiteName: string
    campsiteRateCount: number
    campsiteRating: number
    reviewId?: string
  }>(),
  { reviewId: undefined },
)

const emit = defineEmits(['data-fetched'])
const sorting: Ref<string> = ref(DEFAULT_SORTING)
const selectedFilters: Ref<Filters> = ref({})
const selectedFilterCounts: Ref<FilterCounts> = ref({
  groupType: {},
  language: {},
  unitType: {},
})

const appliedFilters: Ref<Filters> = ref({})
const appliedFilterCounts: Ref<FilterCounts> = ref({
  groupType: {},
  language: {},
  unitType: {},
})

const minReviewsCountForScore = 3
const reviewsIsVisible: Ref<boolean> = ref(false)
const selectedRating: Ref<number | null> = ref(null)
const reviewDist: Ref<DistRating[]> = ref([])

const results: Ref<Review[]> = ref([])
const reviewsCount: Ref<number> = ref(0)
const currentPage: Ref<number> = ref(0)
const totalPages: Ref<number> = ref(0)
const summary: Ref<Summary | null> = ref(null)
const summaryText: Ref<string | null> = ref(null)
const themeBreakdown: Ref<ThemeBreakdown | null> = ref(null)
const loading: Ref<boolean> = ref(false)
const errorText: Ref<string> = ref('')

const ratingBreakdown: Ref<RatingBreakdown | null> = ref(null)
const filterOptions: Ref<CampsiteReviewFilterOption[]> = ref([])
const allowScrollTo: Ref<boolean> = ref(false)
const shouldFetchSingleReview: Ref<boolean> = ref(false)
const singleReviewExists: Ref<boolean> = ref(false)
const reviewPhotosInline: Ref<PhotoResult[]> = ref([])
const reviewPhotosFull: Ref<PhotoResult[]> = ref([])
const reviewImagesLoading = ref(false)

const { $puScrollTo, $isDesktop } = useNuxtApp()
const scrollOffset = $isDesktop ? -75 : -23

watch(appliedFilters, () => {
  selectedFilters.value = appliedFilters.value
})

const { data, error } = await useAsyncData(
  `CampsiteReviewsNew-${props.campsiteSlug}-${props.reviewId}-${currentLanguage}`,
  async () => {
    const [reviewsData, reviewPhotosInlineData] = await Promise.all([
      getReviewsData(),
      fetchInlineReviewPhotos(),
    ])
    return {
      reviewsData,
      reviewPhotosInlineData,
    }
  },
  // this stops the double fetch from server and client
  {
    server: true,
    lazy: process.client,
    dedupe: 'cancel',
    getCachedData: (key, nuxt) => nuxt.payload.data[key] ?? undefined,
  },
)

watch(
  error,
  (e) => {
    if (e) {
      errorText.value = useI18n().t(
        'Unable to load reviews. Please try again.',
      ) as string
    }
  },
  { immediate: true },
)

watch(
  data,
  (newData) => {
    if (newData && newData.reviewsData) {
      if (props.reviewId) {
        shouldFetchSingleReview.value = true
      }
      setReviewsDataVars(newData.reviewsData)
      void nextTick(() => emit('data-fetched'))
    }
    if (newData && newData.reviewPhotosInlineData) {
      reviewPhotosInline.value = newData.reviewPhotosInlineData
    }
  },
  { immediate: true },
)

onMounted(async () => {
  const filterOptionsRepository =
    new AjaxCampsiteReviewFilterOptionsRepository()
  try {
    const options = await filterOptionsRepository.readAll(currentLanguage)
    filterOptions.value = options
    scrollToReviewsList()
  } catch (error: unknown) {
    // Use structured Sentry reporting for filter options API errors
    reportAppError(error, 'reviews component filter options', {
      campsiteSlug: props.campsiteSlug,
      language: currentLanguage,
      errorName: error instanceof Error ? error.name : 'UnknownError',
    })
  }
})

async function updateSelectedFiltersAndCounts(filters: Filters) {
  try {
    const repo = new ReviewsRepositoryFetch()
    const data = await repo.readAll(currentLanguage, {
      campsiteSlug: props.campsiteSlug,
      order: sorting.value,
      filters,
    })
    selectedFilterCounts.value = data.filterCounts
    selectedFilters.value = filters
  } catch (error: unknown) {
    // Use structured Sentry reporting for filter counts API errors
    if (
      error instanceof Error &&
      (error.name === 'ReviewsFetchError' ||
        error.name === 'ReviewsValidationError')
    ) {
      reportApiError(error, 'reviews component filter counts', {
        campsiteSlug: props.campsiteSlug,
        filters: filters,
        sorting: sorting.value,
      })
    } else {
      // Handle unexpected errors
      reportAppError(error, 'reviews component filter counts', {
        campsiteSlug: props.campsiteSlug,
        errorName: error instanceof Error ? error.name : 'UnknownError',
      })
    }
  }
}

function resetFiltersAndCounts() {
  selectedFilterCounts.value = appliedFilterCounts.value
  selectedFilters.value = appliedFilters.value
}

async function fetchReviews() {
  loading.value = true
  errorText.value = ''
  try {
    const data = await getReviewsData()
    setReviewsDataVars(data)
  } catch {
    errorText.value = useI18n().t(
      'Unable to load reviews. Please try again.',
    ) as string
  } finally {
    loading.value = false
  }
}

function setReviewsDataVars({ allReviews, singleReview }) {
  singleReviewExists.value = !!singleReview
  reviewDist.value = allReviews.review_dist
  if (summary.value === null) {
    // Monitor missing summary data for debugging
    if (!allReviews.summary) {
      reportAppError(
        new Error('Reviews API returned incomplete data: missing summary'),
        'reviews component data integrity',
        {
          hasReviewDist: !!allReviews.review_dist,
          reviewsCount: allReviews.count || 0,
          campsiteSlug: props.campsiteSlug || 'unknown',
          responseKeys: Object.keys(allReviews || {}),
        },
      )
    }

    // Defensive programming: handle missing summary data gracefully
    summary.value = {
      revisit: allReviews.summary?.rate_revisit || 0,
      recommend: allReviews.summary?.rate_recommend || 0,
    }
  }
  summaryText.value = allReviews.summary_text || null
  themeBreakdown.value = allReviews.themeBreakdown
  reviewsCount.value = allReviews.count
  totalPages.value = allReviews.num_pages
  currentPage.value = allReviews.page
  selectedFilterCounts.value = allReviews.filterCounts
  appliedFilterCounts.value = allReviews.filterCounts
  ratingBreakdown.value = getRatingBreakdown(allReviews.ratings)
  results.value = getReviewsList(allReviews.reviews, singleReview)
  shouldFetchSingleReview.value = false
}

async function fetchAllReviews(): Promise<ReviewsResponse> {
  try {
    const repo = new ReviewsRepositoryFetch()
    return await repo.readAll(currentLanguage, {
      campsiteSlug: props.campsiteSlug,
      order: sorting.value,
      filters: appliedFilters.value,
      page: currentPage.value,
    })
  } catch (error: unknown) {
    // Use structured Sentry reporting for API errors
    if (
      error instanceof Error &&
      (error.name === 'ReviewsFetchError' ||
        error.name === 'ReviewsValidationError')
    ) {
      reportApiError(error, 'reviews component fetch all', {
        campsiteSlug: props.campsiteSlug,
        sorting: sorting.value,
        currentPage: currentPage.value,
        appliedFilters: appliedFilters.value,
      })
    } else {
      // Handle unexpected errors
      reportAppError(error, 'reviews component fetch all', {
        campsiteSlug: props.campsiteSlug,
        errorName: error instanceof Error ? error.name : 'UnknownError',
      })
    }
    // Return safe empty response to prevent crashes
    return {
      reviews: [],
      count: 0,
      num_pages: 1,
      page: 1,
      limit: 50,
      review_dist: [],
      filterCounts: {
        groupType: {},
        language: {},
        unitType: {},
      },
      summary: {
        rate_revisit: 0,
        rate_recommend: 0,
      },
      summary_text: null,
      themeBreakdown: {
        top: null,
        themes: [],
      },
      ratings: {
        rateOverall: { percent: 0, count: 0, rate: 0 },
        rateLocation: { percent: 0, count: 0, rate: 0 },
        rateValue: { percent: 0, count: 0, rate: 0 },
        rateQuality: { percent: 0, count: 0, rate: 0 },
        rateClean: { percent: 0, count: 0, rate: 0 },
        rateFacilities: { percent: 0, count: 0, rate: 0 },
        rateService: { percent: 0, count: 0, rate: 0 },
        rateChildFriendly: { percent: 0, count: 0, rate: 0 },
        rateWifi: { percent: 0, count: 0, rate: 0 },
      },
      most_popular_theme: {
        id: 0,
        name: '',
        mean: 0,
      },
    }
  }
}

async function fetchReviewsWithPhotos(
  limit?: number,
): Promise<{ reviews: Review[] } | undefined> {
  try {
    const repo = new ReviewPhotosFetch()
    const response = await repo.readAll(currentLanguage, {
      campsiteSlug: props.campsiteSlug,
      order: sorting.value,
      filters: { ...appliedFilters.value, has_photos: true },
      page: 1,
      limit,
    })
    return response
  } catch (error: unknown) {
    // Use structured Sentry reporting for review photos API errors
    reportAppError(error, 'reviews component fetch photos', {
      campsiteSlug: props.campsiteSlug,
      limit: limit || 'undefined',
      sorting: sorting.value,
      errorName: error instanceof Error ? error.name : 'UnknownError',
    })
    return undefined
  }
}

async function fetchInlineReviewPhotos() {
  reviewImagesLoading.value = true
  reviewPhotosInline.value = []
  const reviewsWithPhotos = await fetchReviewsWithPhotos(20)
  if (reviewsWithPhotos) {
    const photos = reviewsWithPhotos.reviews
      .map((review) => review.photos)
      .flat()
    reviewPhotosInline.value = photos
  }
  reviewImagesLoading.value = false
  return reviewPhotosInline.value
}

async function fetchReviewPhotos() {
  reviewPhotosFull.value = []
  const reviewsWithPhotos = await fetchReviewsWithPhotos(500)
  if (reviewsWithPhotos) {
    const photos = reviewsWithPhotos.reviews
      .map((review) => review.photos)
      .flat()
    reviewPhotosFull.value = photos
  }
}

async function fetchSingleReview(): Promise<Review | null> {
  try {
    const repo = new ReviewsRepositoryFetch()
    const review = await repo.readOneById(currentLanguage, {
      campsiteSlug: props.campsiteSlug,
      id: props.reviewId!,
    })
    return review ?? null
  } catch (error: unknown) {
    // Use structured Sentry reporting for API errors
    if (
      error instanceof Error &&
      (error.name === 'ReviewsFetchError' ||
        error.name === 'ReviewsValidationError')
    ) {
      reportApiError(error, 'reviews component fetch single', {
        campsiteSlug: props.campsiteSlug,
        reviewId: props.reviewId,
      })
    } else {
      // Handle unexpected errors
      reportAppError(error, 'reviews component fetch single', {
        campsiteSlug: props.campsiteSlug,
        reviewId: props.reviewId,
        errorName: error instanceof Error ? error.name : 'UnknownError',
      })
    }
    return null
  }
}

async function getReviewsData() {
  let allReviews: ReviewsResponse
  let singleReview: Review | null = null
  if (props.reviewId) {
    ;[allReviews, singleReview] = await Promise.all([
      fetchAllReviews(),
      fetchSingleReview(),
    ])
  } else {
    allReviews = await fetchAllReviews()
  }
  return { allReviews, singleReview }
}

function getReviewsList(allReviews: Review[], singleReview: Review | null) {
  if (singleReview && shouldFetchSingleReview.value) {
    const reviews = [singleReview]
    reviews.push(...allReviews.filter((r) => r.id !== singleReview.id))
    return reviews
  }
  return allReviews
}

function scrollToReviewsList() {
  if (allowScrollTo.value) {
    void nextTick(() => {
      $puScrollTo('[data-reviews-scroll-target]', {
        duration: 300,
        offset: scrollOffset,
      })
    })
  }
}

function scrollToFilters() {
  void nextTick(() => {
    $puScrollTo('[data-reviews-filters-scroll-target]', {
      duration: 300,
      offset: scrollOffset,
    })
  })
}

function getRatingBreakdown(ratings: Ratings): RatingBreakdown {
  return Object.keys(ratings).reduce((acc, current) => {
    acc[current] = ratings[current].average
    return acc
  }, {} as RatingBreakdown)
}

async function setRating(rating: number | null) {
  selectedRating.value = rating
  if (appliedFilters.value.rating && appliedFilters.value.rating === rating) {
    delete appliedFilters.value.rating
    selectedRating.value = null
    await Promise.all([fetchReviews(), fetchInlineReviewPhotos()])
  } else {
    await updateFiltersAndSort({ rating })
  }
}

async function updateFiltersAndSort(changes: SortAndFilters) {
  if (!Object.keys(changes)?.length) {
    return
  }
  const { sorting, ...filters } = changes
  if (sorting) {
    setSort(sorting)
  }
  setFilters(filters)
  currentPage.value = 1
  await Promise.all([fetchReviews(), fetchInlineReviewPhotos()])
  scrollToFilters()
}

function setSort(selectedSorting: string) {
  sorting.value = selectedSorting
}

function setFilters(filters: Filters) {
  appliedFilters.value = Object.assign({}, appliedFilters.value, {
    ...filters,
  })
}

async function resetFilters() {
  appliedFilters.value = {}
  selectedFilters.value = appliedFilters.value
  selectedRating.value = null
  sorting.value = DEFAULT_SORTING
  currentPage.value = 1
  await Promise.all([fetchReviews(), fetchInlineReviewPhotos()])
}

async function changePage(page: number) {
  currentPage.value = page

  allowScrollTo.value = true
  await fetchReviews()
  await nextTick()

  $puScrollTo('[data-reviews-scroll-target]', {
    duration: 300,
    offset: scrollOffset,
  })
}

const paginationData = computed<PaginationData>(() => ({
  currentPage: currentPage.value,
  totalPages: totalPages.value,
  totalCount: reviewsCount.value,
}))

const isFilterApplied = computed<boolean>(
  () => Object.keys(appliedFilters.value).length > 0,
)

const displayState = computed<string>(() => {
  if (loading.value) {
    return 'loading'
  }
  if (results.value.length === 0) {
    return 'no-reviews'
  }
  return 'show'
})

const distribution = computed<Rating[]>(() => {
  return Object.entries(reviewDist.value)
    .map(([key, value]) => ({
      percent: value.percentage,
      count: value.count,
      rate: Number(key) + 1,
    }))
    .reverse()
})

const sortingTranslatedName = computed<string>(() => {
  const sorts = {
    '-visit_date': useI18n().t('Arrival date (latest first)') as string,
    rate_overall: useI18n().t('Overall rating (lowest first)') as string,
    '-rate_overall': useI18n().t('Overall rating (highest first)') as string,
  }
  return sorts[sorting.value]
})

const filterNames = computed<string[]>(() => {
  const names = filterOptions.value
    .filter(({ name, type }) => appliedFilters.value[type] === name)
    .map(({ value }) => value)
  if (appliedFilters.value.rating) {
    names.push(
      (useI18n().t('Rating') as string) + ` (${appliedFilters.value.rating})`,
    )
  }
  if (appliedFilters.value.has_photos === true) {
    names.push(useI18n().t('With photos') as string)
  }
  return names
})

const isSummaryVisible = computed<boolean>(() => {
  return results.value.length > 0 && displayState.value !== 'no-reviews'
})

const isDistributionVisible = computed<boolean>(() => {
  return (
    reviewsCount.value >= minReviewsCountForScore &&
    displayState.value !== 'no-reviews'
  )
})

const reviewsHash = computed<string>(() => {
  return singleReviewExists.value
    ? `reviews_${props.reviewId}`
    : 'no-single-review'
})

function reviewsVisibilityChanged(isVisible: boolean) {
  reviewsIsVisible.value = isVisible
}

function sortFilterClickHandler() {
  const filterButton = document.querySelector(
    '.reviews-filter-button',
  ) as HTMLElement
  if (filterButton) {
    filterButton.click()
  }
}
</script>

<style scoped>
.reviews-action-buttons {
  display: flex;
  justify-content: space-between;
  align-items: stretch;
  gap: 1em;
  padding: 0 1em;
  margin: 1em 0;
  flex-wrap: wrap;
}

.reviews-action-buttons :deep(.login-modal) {
  flex: 0 1 auto;
}

.reviews-action-buttons :deep(.modal-trigger-wrapper) {
  height: 100%;
}

.applied-filters {
  color: var(--colour-dark-blue);
  font-size: var(--body);
  line-height: 1.4;
  margin-bottom: 1em;
  padding: 0 0.75em 0;
  text-align: center;
  text-wrap-style: pretty;
  width: 100%;
}

.applied-filters span:after {
  content: ', ';
}

.applied-filters span:last-of-type:after {
  content: none;
}

.reset-icon {
  margin: 0 0.75em 0.15em 0.15em;
  cursor: pointer;
  display: inline-flex;
}

.status {
  font-size: var(--body);
  text-align: center;
  margin: 1em auto;
}

.no-reviews .reviews-action-buttons {
  justify-content: center;
}

.campsite-reviews.desktop .no-reviews .status {
  font-size: inherit;
  text-align: left;
}

.campsite-reviews.desktop .no-reviews .reviews-action-buttons {
  justify-content: start;
}

.error {
  color: var(--colour-red);
}

.list-header {
  align-items: end;
  color: var(--colour-dark-blue);
  display: flex;
  font-size: var(--small);
  gap: 2em;
  justify-content: space-between;
  font-size: var(--small);
  color: var(--colour-dark-blue);
  padding: 0 0.75em 0.7em 1em;
  gap: 2em;
}

.reviews-page-count-summary {
  white-space: nowrap;
}

.reviews-sorting-name {
  text-align: right;
  cursor: pointer;
  text-wrap-style: pretty;
  line-height: 1.1;
}

.reviews-sorting-name img {
  display: inline-flex;
}

.campsite-reviews :deep(.single-review .user-avatar) {
  border: none;
}

.campsite-reviews :deep(.single-review .user-avatar) {
  background: #555;
}

.campsite-reviews :deep(.single-review:nth-of-type(2n) .user-avatar) {
  background: #d06406;
}

.campsite-reviews :deep(.single-review:nth-of-type(3n) .user-avatar) {
  background: #a459b5;
}

.campsite-reviews :deep(.single-review:nth-of-type(4n) .user-avatar) {
  background: #ab0100;
}

.campsite-reviews :deep(.single-review:nth-of-type(5n) .user-avatar) {
  background: var(--colour-form-blue);
}

.pagination {
  padding-bottom: 0.7em;
}

.campsite-reviews-gallery {
  margin-top: 1em;
}

.campsite-reviews-gallery-loader {
  min-height: 3em;
  display: flex;
  justify-content: center;
}

.desktop .campsite-reviews-gallery-loader {
  min-height: 7.5em;
  align-items: center;
}

.campsite-reviews.desktop .list-header {
  padding: 0.5em 0 0.3em;
  font-size: inherit;
}

.campsite-reviews.desktop .reset-icon {
  margin: 0.25em 1em 0.5em 0.15em;
  width: 1.5em;
  height: 1.5em;
}

@media (hover: hover) {
  .campsite-reviews.desktop .reset-icon:hover {
    filter: brightness(0.5);
  }
  .write-a-review:hover {
    background: #ced6da;
  }
}

.campsite-reviews.desktop .applied-filters {
  margin: 0;
}

.campsite-reviews.desktop .reviews-filters-wrapper {
  display: flex;
  align-items: center;
  gap: 3em;
  justify-content: start;
}

.campsite-reviews.desktop .applied-filters {
  font-size: inherit;
  text-align: left;
  padding: 0;
}

.campsite-reviews.desktop .reviews-action-buttons {
  flex-direction: row-reverse;
  align-items: center;
  padding: 0;
}

.campsite-reviews.desktop
  .reviews-action-buttons
  :deep(.modal-trigger-wrapper) {
  height: auto;
}

.campsite-reviews.desktop .reviews-action-buttons :deep(.write-a-review) {
  padding: 0.75em 1em;
  font-size: inherit;
}
</style>
